/* HFD 2026 runtime.
   One IntersectionObserver-gated requestAnimationFrame loop drives every
   scroll-linked effect on the page. There is no window scroll listener
   anywhere in this file, by design. */
(function () {
  'use strict';
  if (window.HFD && window.HFD.v >= 2026) return;

  var reduceQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = function () { return reduceQ.matches; };
  var clamp = function (v, a, b) { return v < a ? a : (v > b ? b : v); };

  /* ---------------- driver ---------------- */
  var live = [];
  var rafId = 0;

  function pump() {
    rafId = 0;
    var vh = window.innerHeight;
    for (var i = 0; i < live.length; i++) {
      var rec = live[i];
      try { rec.onFrame(rec.el.getBoundingClientRect(), vh); } catch (e) {}
    }
    if (live.length) rafId = requestAnimationFrame(pump);
  }
  function wake() { if (!rafId && live.length) rafId = requestAnimationFrame(pump); }

  var gate = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      var rec = e.target.__hfdRec;
      if (!rec) continue;
      var at = live.indexOf(rec);
      if (e.isIntersecting) { if (at < 0) live.push(rec); }
      else if (at >= 0) { live.splice(at, 1); if (rec.onIdle) rec.onIdle(); }
    }
    wake();
  }, { rootMargin: '30% 0px 30% 0px', threshold: 0 });

  function register(el, onFrame, onIdle) {
    if (el.__hfdRec) return function () {};
    var rec = { el: el, onFrame: onFrame, onIdle: onIdle };
    el.__hfdRec = rec;
    gate.observe(el);
    return function () {
      gate.unobserve(el);
      var at = live.indexOf(rec); if (at >= 0) live.splice(at, 1);
      el.__hfdRec = null;
    };
  }

  /* ---------------- resize bus ---------------- */
  var resizers = [];
  var rt = 0;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(function () {
      for (var i = 0; i < resizers.length; i++) { try { resizers[i](); } catch (e) {} }
      wake();
    }, 140);
  }, { passive: true });
  window.addEventListener('orientationchange', function () {
    clearTimeout(rt);
    rt = setTimeout(function () {
      for (var i = 0; i < resizers.length; i++) { try { resizers[i](); } catch (e) {} }
    }, 260);
  }, { passive: true });
  function onResize(fn) { resizers.push(fn); return fn; }

  /* ---------------- reveal ---------------- */
  var revealIO = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (!e.isIntersecting) continue;
      var tall = e.boundingClientRect.height > window.innerHeight * 0.75;
      if (e.intersectionRatio >= 0.3 || tall) {
        e.target.classList.add('is-in');
        revealIO.unobserve(e.target);
      }
    }
  }, { threshold: [0, 0.3], rootMargin: '0px 0px -6% 0px' });

  function reveals(root) {
    var groups = (root || document).querySelectorAll('[data-hfd-stagger]');
    for (var g = 0; g < groups.length; g++) {
      var kids = groups[g].querySelectorAll('[data-hfd-reveal]');
      var step = parseInt(groups[g].getAttribute('data-hfd-stagger'), 10) || 60;
      for (var k = 0; k < kids.length; k++) {
        if (!kids[k].style.getPropertyValue('--hfd-d')) {
          kids[k].style.setProperty('--hfd-d', (k * step) + 'ms');
        }
      }
    }
    var all = (root || document).querySelectorAll('[data-hfd-reveal]:not(.is-in)');
    for (var i = 0; i < all.length; i++) revealIO.observe(all[i]);
  }

  /* ---------------- film ---------------- */
  function film(root) {
    if (root.__hfdFilm) return;
    root.__hfdFilm = 1;

    var canvas  = root.querySelector('[data-film-canvas]');
    var spacer  = root.querySelector('[data-film-spacer]');
    if (!canvas || !spacer) return;

    var ctx      = canvas.getContext('2d', { alpha: true });
    var chapters = [].slice.call(root.querySelectorAll('[data-film-chapter]'));
    var tplWide  = root.getAttribute('data-tpl') || '';
    var tplNarrow= root.getAttribute('data-tpl-mobile') || tplWide;
    var F0       = parseInt(root.getAttribute('data-start'), 10) || 1;
    var N        = parseInt(root.getAttribute('data-count'), 10) || 1;
    var PAD      = parseInt(root.getAttribute('data-pad'), 10) || 4;
    var PACE     = parseFloat(root.getAttribute('data-pace')) || 3;

    var tpl = '', imgs = [], state = [], want = 0, shown = -1, glide = 0, ready = false;

    function pick() { return (window.innerWidth < 750 && tplNarrow) ? tplNarrow : tplWide; }

    function src(i) {
      var n = String(F0 + i);
      while (n.length < PAD) n = '0' + n;
      return tpl.replace('{frame}', n);
    }

    function load(i) {
      if (i < 0 || i >= N || state[i]) return;
      state[i] = 1;
      var im = new Image();
      im.decoding = 'async';
      im.src = src(i);
      var settle = function () {
        if (state[i] !== 1) return;
        state[i] = 2; imgs[i] = im;
        if (!ready) { ready = true; root.classList.add('is-ready'); }
        if (i === Math.round(glide)) paint(i);
      };
      if (im.decode) im.decode().then(settle, function () { state[i] = 0; });
      else { im.onload = settle; im.onerror = function () { state[i] = 0; }; }
    }

    function prefetch(i) {
      load(i);
      for (var k = 1; k <= 22; k++) { load(i + k); if (k <= 6) load(i - k); }
    }

    function sizeCanvas() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.round(canvas.clientWidth * dpr);
      var h = Math.round(canvas.clientHeight * dpr);
      if (!w || !h) return;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        shown = -1;
      }
    }

    function paint(i) {
      var j = clamp(i, 0, N - 1);
      while (j > 0 && state[j] !== 2) j--;
      if (state[j] !== 2 || j === shown) return;
      shown = j;
      var im = imgs[j], cw = canvas.width, ch = canvas.height;
      if (!cw || !ch || !im.naturalWidth) return;
      var s = Math.max(cw / im.naturalWidth, ch / im.naturalHeight);
      var w = im.naturalWidth * s, h = im.naturalHeight * s;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(im, (cw - w) / 2, (ch - h) / 2, w, h);
    }

    function chapterSync(idx) {
      var f = F0 + idx;
      for (var c = 0; c < chapters.length; c++) {
        var el = chapters[c];
        var a = parseInt(el.getAttribute('data-from'), 10);
        var b = parseInt(el.getAttribute('data-to'), 10);
        var on = f >= a && f <= b;
        if (on !== el.__on) { el.__on = on; el.classList.toggle('is-on', on); }
      }
    }

    function layout() {
      var next = pick();
      if (next !== tpl) {
        tpl = next; imgs = []; state = []; shown = -1; ready = false;
        root.classList.remove('is-ready');
      }
      spacer.style.height = reduced() ? '100dvh' : (Math.round(PACE * 100) + 'dvh');
      sizeCanvas();
      if (reduced()) {
        want = Math.round(N * 0.55); glide = want;
        prefetch(want); chapterSync(want); paint(want);
      } else {
        prefetch(Math.max(0, Math.round(glide)));
        paint(Math.round(glide));
      }
    }

    function frame(rect, vh) {
      if (reduced()) return;
      var span = rect.height - vh;
      if (span <= 0) return;
      var p = clamp(-rect.top / span, 0, 1);
      want = Math.round(p * (N - 1));
      if (glide < 0) glide = want;
      var d = want - glide;
      glide = Math.abs(d) < 0.55 ? want : glide + d * 0.32;
      var idx = Math.round(glide);
      prefetch(want);
      chapterSync(idx);
      paint(idx);
    }

    glide = 0;
    layout();
    onResize(layout);
    if (reduceQ.addEventListener) reduceQ.addEventListener('change', layout);
    register(root, frame);
  }

  /* ---------------- horizontal pan ---------------- */
  function pan(root) {
    if (root.__hfdPan) return;
    root.__hfdPan = 1;
    var spacer = root.querySelector('[data-pan-spacer]');
    var track  = root.querySelector('[data-pan-track]');
    if (!spacer || !track) return;
    var span = 0, glide = 0, target = 0, started = false;

    function narrow() { return window.innerWidth < 768 || reduced(); }

    function layout() {
      if (narrow()) {
        spacer.style.height = 'auto';
        track.style.transform = '';
        span = 0;
        return;
      }
      track.style.transform = 'translate3d(0,0,0)';
      span = Math.max(0, track.scrollWidth - window.innerWidth + 32);
      spacer.style.height = 'calc(100dvh + ' + Math.round(span) + 'px)';
      glide = target = 0;
      started = false;
    }

    function frame(rect, vh) {
      if (narrow() || span <= 0) return;
      var total = rect.height - vh;
      if (total <= 0) return;
      var p = clamp(-rect.top / total, 0, 1);
      target = p * span;
      if (!started) { glide = target; started = true; }
      var d = target - glide;
      glide = Math.abs(d) < 0.4 ? target : glide + d * 0.16;
      track.style.transform = 'translate3d(' + (-glide).toFixed(2) + 'px,0,0)';
      root.style.setProperty('--hfpn-p', (glide / span).toFixed(4));
    }

    layout();
    onResize(layout);
    register(root, frame);
  }

  /* ---------------- parallax (subtle, transform only) ---------------- */
  function drift(el) {
    if (el.__hfdDrift) return;
    el.__hfdDrift = 1;
    var amt = parseFloat(el.getAttribute('data-hfd-drift')) || 40;
    register(el, function (rect, vh) {
      if (reduced()) return;
      var mid = rect.top + rect.height / 2;
      var p = clamp((vh / 2 - mid) / vh, -1, 1);
      el.style.transform = 'translate3d(0,' + (p * amt).toFixed(2) + 'px,0)';
    });
  }

  /* ---------------- boot ---------------- */
  function boot(root) {
    var scope = root || document;
    var f = scope.querySelectorAll('[data-hfd-film]');
    for (var i = 0; i < f.length; i++) film(f[i]);
    var p = scope.querySelectorAll('[data-hfd-pan]');
    for (var j = 0; j < p.length; j++) pan(p[j]);
    var d = scope.querySelectorAll('[data-hfd-drift]');
    for (var k = 0; k < d.length; k++) drift(d[k]);
    reveals(scope);
    wake();
  }

  window.HFD = {
    v: 2026,
    register: register,
    onResize: onResize,
    reveals: reveals,
    film: film,
    pan: pan,
    boot: boot,
    clamp: clamp,
    reduced: reduced
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-hfd-film],[data-hfd-pan]');
    if (el && el.__hfdRec) { gate.unobserve(el); var at = live.indexOf(el.__hfdRec); if (at >= 0) live.splice(at, 1); }
  });
})();
